import ToggleField from "@/shared/components/ui/Form/ToggleField";
import {
  cleanMessageTemplates,
  CONTENT_GENERATION,
  CONTENT_GENERATION_COPY,
  getContentGeneration,
  TEMPLATE_CHANNELS,
} from "@/features/campaigns/utils/stepContentMode";
import CampaignMessageTemplatesModal from "../Messaging/CampaignMessageTemplatesModal";
import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { Spinner } from "@heroui/react";
import { Pen, Save } from "lucide-react";
import FormFields from "../FormFields";
import PitchModal from "./PitchModal";
import AudienceModal from "./AudienceModal";
import { campaignLanguageOptions } from "@/shared/utils/utils";
import {
  COMPANY_SIZE_LABEL_BY_RANGE,
  normalizeCompanySizeList,
} from "@/shared/utils/onboardingConfig";
import { useMainStore } from "@/stores";
import { ChipInput } from "@/shared/components/ui/Input/chipInput";
import MessageStyleModal from "./MessageStyleModal";
import { CAMPAIGN_MESSAGE_STYLE_OPTIONS } from "@/shared/utils/aiConfig";
import CampaignIntegrations from "./CampaignIntegrations";

const CampaignSettingForm = ({
  campaignDetails,
  register,
  control,
  campaignTitle,
  notificationEmail,
  isSavingTitle,
  hasTitleChanged,
  handleSaveTitle,
  canSaveNotificationEmail,
  notificationEmailError,
  handleSaveNotificationEmail,
  isSavingNotificationEmail,
  handleEditClickPitch,
  handleEditClickTargetAudience,
  handleEditClickMessageStyle,
  showPitchModal,
  showTargetAudienceModal,
  showMessageStyleModal,
  handleClosePitchModal,
  handleCloseTargetAudienceModal,
  handleCloseMessageStyleModal,
  handleMessageStyleSaveClick,
  onCampaignLanguageChange,
  pitchData,
  audienceData,
  registerPitchSubmitHandler,
  registerAudienceSubmitHandler,
  companyAssets,
  handleAssetsChange,
  setCompanyAssets: _setCompanyAssets,
  campaignId,
  handleEmailDomainChange: _handleEmailDomainChange,
  setAudienceData,
  handleAudienceSaveClick,
  handlePitchSaveClick,
  onPitchDataChange,
  isSavingAudience,
  isSavingPitch,
  isSavingMessageStyle,
  messageStyle = [],
  onContentGenerationChange,
  showMessageTemplatesModal = false,
  onEditMessageTemplates,
  onCloseMessageTemplatesModal,
  onSaveMessageTemplates,
  isSavingMessageTemplates = false,
  setValue,
  services = [],
  onServicesChange,
  errors: formErrors = {},
}) => {
  const { companyProfile } = useMainStore();
  const contentGeneration = getContentGeneration(campaignDetails);
  const savedTemplates = cleanMessageTemplates(campaignDetails?.message_templates);
  const templatesSummary = TEMPLATE_CHANNELS.filter((channel) => savedTemplates[channel.key])
    .map((channel) => channel.title)
    .join(", ");
  const companyServicesOptions = companyProfile?.company_services || [];
  const pitchDisplayValue = useMemo(() => {
    if (campaignDetails?.define_pitch?.valueProposition) {
      return campaignDetails.define_pitch.valueProposition;
    }
    return "Define Pitch";
  }, [campaignDetails?.define_pitch?.valueProposition]);

  // Memoize audience display value
  const audienceDisplayValue = useMemo(() => {
    if (
      campaignDetails?.target_audience?.roles?.length > 0 ||
      campaignDetails?.target_audience?.targetIndustries?.length > 0
    ) {
      const sizeArray = normalizeCompanySizeList(
        campaignDetails?.target_audience?.companySize,
      );

      return sizeArray.length > 0
        ? sizeArray
            .map((range) => COMPANY_SIZE_LABEL_BY_RANGE[range] || range)
            .join(", ") + " Business"
        : "Define Audience";
    }
    return "Define Audience";
  }, [campaignDetails?.target_audience]);

  const messageStyleDisplayValue = useMemo(() => {
    const styles =
      messageStyle?.length > 0
        ? messageStyle
        : (campaignDetails?.messageStyle ??
          campaignDetails?.target_audience?.messageStyle ??
          []);
    if (!Array.isArray(styles) || styles.length === 0) {
      return "Define Message Style";
    }
    return styles
      .map(
        (key) =>
          CAMPAIGN_MESSAGE_STYLE_OPTIONS.find((o) => o.key === key)?.title ??
          key,
      )
      .join(", ");
  }, [
    messageStyle,
    campaignDetails?.messageStyle,
    campaignDetails?.target_audience?.messageStyle,
  ]);

  return (
    <div className="flex flex-col gap-[22px] w-full p-1.5 h-full">
      <FormFields
        label="Campaign Title"
        {...register("campaignTitle")}
        placeholder="Enter Campaign Title"
        type="text"
        value={campaignTitle}
        showRightIcon="condition"
        showRightIconCondition={hasTitleChanged}
        rightIcon={
          <div
            className="flex gap-0.5 items-center text-gray-500 text-xs font-normal cursor-pointer hover:text-secondary transition-colors"
            onClick={handleSaveTitle}
          >
            {isSavingTitle ? (
              <Spinner size="sm" color="secondary" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isSavingTitle ? "Saving" : "Save"}
          </div>
        }
      />

      <div className="flex flex-col gap-1 w-full">
        <label className="text-[12.5px] font-medium text-gray-800 dark:text-gray-200">
          Campaign services
        </label>
        <ChipInput
          placeholder="Select from suggestion or type new & press enter to add it!"
          defaultChips={services}
          suggestions={companyServicesOptions}
          dropdownOptions={companyServicesOptions}
          onChange={(value) => {
            setValue("services", value, {
              shouldValidate: true,
              shouldDirty: true,
            });
            onServicesChange?.(value);
          }}
          errors={formErrors?.services}
        />
        {formErrors?.services?.message && (
          <p className="text-[0.6375rem] text-error-500">
            {formErrors.services.message}
          </p>
        )}
      </div>

      <CampaignIntegrations campaignId={campaignId} />

      <FormFields
        label="Define Your Pitch"
        defaultValue={pitchDisplayValue}
        value={pitchDisplayValue}
        rightIcon={
          <div
            className="flex gap-0.5 items-center text-gray-500 text-xs font-normal cursor-pointer"
            onClick={handleEditClickPitch}
          >
            <Pen className="w-4 h-4" />
            Customize
          </div>
        }
        placeholder="Define Your Pitch here"
        showRightIcon="hover"
        type="text"
        editable={false}
      />

      <FormFields
        label="Define Your Target Audience"
        defaultValue={audienceDisplayValue}
        value={audienceDisplayValue}
        rightIcon={
          <div
            className="flex gap-0.5 items-center text-gray-500 text-xs font-normal cursor-pointer"
            onClick={handleEditClickTargetAudience}
          >
            <Pen className="w-4 h-4" />
            Customize
          </div>
        }
        placeholder="Define Your Target Audience"
        showRightIcon="hover"
        type="text"
        editable={false}
      />

      <div className="flex flex-col gap-1 w-full">
        <FormFields
          label="Notification Email"
          {...register("notificationEmail")}
          placeholder="Enter Notification Email"
          type="email"
          value={notificationEmail ?? ""}
          showRightIcon="condition"
          showRightIconCondition={canSaveNotificationEmail}
          rightIcon={
            <div
              className={`flex gap-0.5 items-center text-xs font-normal transition-colors ${
                canSaveNotificationEmail
                  ? "cursor-pointer text-gray-500 hover:text-secondary"
                  : "cursor-not-allowed text-gray-300"
              }`}
              onClick={() => {
                if (!canSaveNotificationEmail || isSavingNotificationEmail)
                  return;
                handleSaveNotificationEmail();
              }}
            >
              {isSavingNotificationEmail ? (
                <Spinner size="sm" color="secondary" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {isSavingNotificationEmail ? "Saving" : "Save"}
            </div>
          }
        />
        {notificationEmailError ? (
          <p className="text-[0.6375rem] text-error-500">
            {notificationEmailError}
          </p>
        ) : null}
      </div>

      <FormFields
        label="Messaging Style"
        defaultValue={messageStyleDisplayValue}
        value={messageStyleDisplayValue}
        rightIcon={
          <div
            className="flex gap-0.5 items-center text-gray-500 text-xs font-normal cursor-pointer"
            onClick={handleEditClickMessageStyle}
          >
            <Pen className="w-4 h-4" />
            Customize
          </div>
        }
        placeholder="Define Message Style"
        showRightIcon="hover"
        type="text"
        editable={false}
      />

      {onContentGenerationChange && (
        <ToggleField
          label="AI-personalized content"
          description={CONTENT_GENERATION_COPY[contentGeneration]}
          checked={contentGeneration !== CONTENT_GENERATION.OFF}
          onChange={onContentGenerationChange}
        />
      )}

      {/* Campaign messages are only for campaigns without AI-written content. */}
      {onEditMessageTemplates && contentGeneration === CONTENT_GENERATION.OFF && (
        <FormFields
          label="Campaign messages"
          defaultValue={templatesSummary}
          value={templatesSummary}
          rightIcon={
            <div
              className="flex gap-0.5 items-center text-gray-500 text-xs font-normal cursor-pointer"
              onClick={onEditMessageTemplates}
            >
              <Pen className="w-4 h-4" />
              Customize
            </div>
          }
          placeholder="No campaign messages yet"
          showRightIcon="hover"
          type="text"
          editable={false}
        />
      )}

      {/* <div className="flex flex-col gap-1 w-full">
        <label className="text-[12.5px] font-medium text-gray-800">
          Email Domains
        </label>
        <ChipInput
          register={register("emailDomain")}
          py={"py-1"}
          type="email"
          minHeight="min-h-[36px]"
          placeholder="Enter valid email domain and press enter to add"
          label="Email Domains"
          className="shadow-sm"
          onChange={(value) => handleEmailDomainChange(value)}
          defaultChips={campaignDetails?.email_domains || []}
        />
      </div> */}
      <div className="">
        <Controller
          name="campaignLanguage"
          control={control}
          render={({ field }) => (
            <FormFields
              label="Campaign Language"
              value={field.value}
              onChange={(keys) => {
                const selectedValue = Array.from(keys)[0];
                if (selectedValue) {
                  field.onChange(selectedValue);
                  onCampaignLanguageChange?.(selectedValue);
                }
              }}
              placeholder="Select Campaign Language"
              variant="select"
              options={campaignLanguageOptions}
            />
          )}
        />
      </div>

      {showTargetAudienceModal && (
        <AudienceModal
          isOpen={showTargetAudienceModal}
          onClose={handleCloseTargetAudienceModal}
          onSave={handleAudienceSaveClick}
          registerSubmitHandler={registerAudienceSubmitHandler}
          onDataChange={setAudienceData}
          audienceData={audienceData}
          isSavingAudience={isSavingAudience}
        />
      )}

      {showPitchModal && (
        <PitchModal
          isOpen={showPitchModal}
          onClose={handleClosePitchModal}
          onSave={handlePitchSaveClick}
          registerSubmitHandler={registerPitchSubmitHandler}
          onDataChange={(data) => onPitchDataChange?.(data)}
          onAssetsChange={handleAssetsChange}
          companyAssets={companyAssets}
          pitchData={pitchData}
          campaignId={campaignId}
          isSavingPitch={isSavingPitch}
        />
      )}

      {showMessageStyleModal && (
        <MessageStyleModal
          isOpen={showMessageStyleModal}
          onClose={handleCloseMessageStyleModal}
          onSave={handleMessageStyleSaveClick}
          messageStyle={messageStyle}
          isSavingMessageStyle={isSavingMessageStyle}
        />
      )}
      {showMessageTemplatesModal && (
        <CampaignMessageTemplatesModal
          isOpen={showMessageTemplatesModal}
          onClose={onCloseMessageTemplatesModal}
          campaignId={campaignId}
          value={savedTemplates}
          onSave={onSaveMessageTemplates}
          isSaving={isSavingMessageTemplates}
          description={
            contentGeneration === CONTENT_GENERATION.OFF
              ? "Every step of a channel sends its message unless the step has its own. Preview it against any lead in this campaign."
              : "Sent for any step AI hasn't written yet, so leads never wait on generation. Preview it against any lead in this campaign."
          }
        />
      )}
    </div>
  );
};

export default CampaignSettingForm;
